
import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Square, Check, Undo, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> & {
    showUndo?: boolean;
    onReset?: () => void;
    variant?: "default" | "toggle";
  }
>(({ className, showUndo, onReset, variant = "default", ...props }, ref) => {
  return (
    <div className="relative">
      <RadioGroupPrimitive.Root
        className={cn(
          "grid gap-2", 
          variant === "toggle" && "flex flex-row items-center justify-start",
          className
        )}
        {...props}
        ref={ref}
      />
      {showUndo && onReset && (
        <Button 
          variant="outline" 
          size="sm" 
          className="absolute -top-8 right-0 flex items-center gap-1"
          onClick={onReset}
        >
          <Undo className="h-4 w-4" /> Undo selection
        </Button>
      )}
    </div>
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    variant?: "default" | "toggle";
  }
>(({ className, children, variant = "default", ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        variant === "default" 
          ? "flex items-center justify-between w-full px-4 py-3 border border-border rounded-md bg-background text-foreground transition-all hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary"
          : "flex items-center justify-center h-10 w-10 rounded-md border border-input bg-background text-foreground transition-all hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        className
      )}
      {...props}
    >
      {variant === "default" ? (
        <>
          {children && <div className="text-foreground font-medium">{children}</div>}
          <div className="flex items-center justify-center h-5 w-5">
            <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
              <Square className="h-5 w-5 text-primary">
                <Check className="h-4 w-4" />
              </Square>
            </RadioGroupPrimitive.Indicator>
            {/* Only show the empty square when not checked */}
            {props["data-state"] !== "checked" && <Square className="h-5 w-5 text-muted-foreground" />}
          </div>
        </>
      ) : (
        <>
          <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
            {props.value === "yes" ? (
              <Check className="h-4 w-4" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </RadioGroupPrimitive.Indicator>
          {props["data-state"] !== "checked" && (
            props.value === "yes" ? (
              <Check className="h-4 w-4 text-muted-foreground" />
            ) : (
              <X className="h-4 w-4 text-muted-foreground" />
            )
          )}
        </>
      )}
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
